import { Building2, Wrench, Scale, BadgeEuro, Megaphone, type LucideIcon } from "lucide-react";
import { VENTE_SECTIONS, type VenteSection, type VenteFieldDef } from "./venteFormFields";
import type { FicheTabAlert } from "@/components/biens/FicheTabBar";

/**
 * Onglets de la fiche VENTE.
 *
 * Le critère de la fiche location ne s'applique pas ici. Là-bas la découpe est
 * TEMPORELLE — ce qui survit au départ du locataire d'un côté, ce qui se remet
 * à zéro à chaque rotation de l'autre. Une vente n'a pas de cycle qui
 * recommence : le bien part une fois.
 *
 * L'axe retenu est le DESTINATAIRE de l'information :
 *   · « Le bien »        → ce qui décrit le bâtiment, du numéro de rue aux
 *                          volumes : c'est le calque de l'onglet « Le bien » de
 *                          la fiche location ;
 *   · « Technique »      → ce que l'acheteur fait inspecter ;
 *   · « Dossier légal »  → ce que le notaire exige pour l'acte ;
 *   · « Prix »           → ce qui se négocie ;
 *   · « Commercialisation » → ce qui part chez l'acheteur potentiel.
 *
 * CINQ onglets. Historique de la décision, pour qu'on ne la rejoue pas une
 * troisième fois :
 *   - option A (5 onglets) à l'origine ;
 *   - option C (6 onglets) le matin du 2026-08-09 — « Le bien » portait 48 des
 *     110 champs, et un onglet de 48 champs est le défaut qu'on venait de
 *     corriger côté location ;
 *   - RETOUR À CINQ le même jour, après revue en navigateur : la mise en page
 *     12 colonnes ramène « Le bien » à une hauteur tenable sans le couper, et
 *     Thibaud veut la MÊME barre d'onglets d'un catalogue à l'autre. Ce qui
 *     rendait l'onglet lourd, c'était la disposition, pas le nombre de champs.
 *
 * Arbitrages posés par défaut, à rediscuter (cf. document de découpage) :
 *   - amiante et attestation du sol vont au DOSSIER LÉGAL et non à l'énergie :
 *     ce sont des obligations du vendeur à l'acte, pas des mesures du bâtiment ;
 *   - la disponibilité va avec le PRIX, les deux se négociant ensemble.
 */
export type VenteTabId = "bien" | "technique" | "legal" | "prix" | "commercialisation";

export const VENTE_TAB_IDS: VenteTabId[] = [
  "bien",
  "technique",
  "legal",
  "prix",
  "commercialisation",
];

export const VENTE_TAB_META: Record<VenteTabId, { labelKey: string; icon: LucideIcon }> = {
  bien: { labelKey: "vente.tabs.bien", icon: Building2 },
  technique: { labelKey: "vente.tabs.technique", icon: Wrench },
  legal: { labelKey: "vente.tabs.legal", icon: Scale },
  prix: { labelKey: "vente.tabs.prix", icon: BadgeEuro },
  commercialisation: { labelKey: "vente.tabs.commercialisation", icon: Megaphone },
};

/** Rattachement de chaque section de `venteFormFields` à son onglet. */
export const VENTE_TAB_OF_SECTION: Record<VenteSection, VenteTabId> = {
  identification: "bien",
  general: "bien",
  copropriete: "bien",
  localisation: "bien",
  interieur: "bien",
  equipements: "bien",
  exterieur: "bien",

  raccordements: "technique",
  energie: "technique",

  urbanisme: "legal",
  certificats: "legal",

  finances: "prix",
  disponibilite: "prix",

  proximite: "commercialisation",
  pointsForts: "commercialisation",
  publication: "commercialisation",
  details: "commercialisation",
  medias: "commercialisation",
};

/** Sections d'un onglet, dans l'ordre d'origine de `VENTE_SECTIONS`. */
export function venteSectionsOfTab(tab: VenteTabId): VenteSection[] {
  return VENTE_SECTIONS.filter((s) => VENTE_TAB_OF_SECTION[s] === tab);
}

/**
 * Garde-fou : toute section doit être rattachée à un onglet, sinon ses champs
 * deviendraient invisibles sans que rien ne le signale. Lève au chargement du
 * module plutôt que de laisser une section disparaître en silence.
 */
const orphelines = VENTE_SECTIONS.filter((s) => !VENTE_TAB_OF_SECTION[s]);
if (orphelines.length > 0) {
  throw new Error(
    `venteTabs : section(s) sans onglet — ${orphelines.join(", ")}. ` +
      "Toute nouvelle section de venteFormFields doit être rattachée ici.",
  );
}

/**
 * Pastilles des onglets de la fiche vente (parité fiche location,
 * `FicheTabBar.alerts`). Chaque champ manquant est compté sur l'onglet de sa
 * section ; les hints vente sont tous « obligatoire », donc rouge. Les clés
 * sans définition rendue (ou garées, `VENTE_FICHE_PARKED`) sont ignorées :
 * une pastille doit pointer un champ que l'utilisateur peut corriger.
 * Retour Thibaud 2026-09-16 : la fiche vente ne passait aucune pastille, un
 * champ encadré en rouge dans un onglet fermé était invisible.
 */
export function venteTabAlerts(
  hints: ReadonlyMap<string, "required">,
  fields: readonly Pick<VenteFieldDef, "key" | "section">[],
  parked: ReadonlySet<string> = new Set(),
): Partial<Record<VenteTabId, FicheTabAlert>> {
  const out: Partial<Record<VenteTabId, FicheTabAlert>> = {};
  for (const key of hints.keys()) {
    if (parked.has(key)) continue;
    const def = fields.find((f) => f.key === key);
    if (!def) continue;
    const tab = VENTE_TAB_OF_SECTION[def.section];
    out[tab] = { count: (out[tab]?.count ?? 0) + 1, level: "required" };
  }
  return out;
}
